import { Autocomplete, Box, Paper, Stack, TextField } from '@mui/material';
import type { ReactNode } from 'react';

import ViewToggle from 'components/v2/common/viewToggle';

import { BILLING_COUNTRIES } from 'data/constants/serviceProviderBilling';
import useTranslate from 'hooks/useTranslate';
import type { BillingView, ProviderOption, ServiceCountryCode } from 'types';

interface BillingScopeToolbarProps {
    view: BillingView;
    onViewChange: (view: BillingView) => void;
    providerOptions: ProviderOption[];
    providersLoading: boolean;
    providerId: number | null;
    onProviderChange: (providerId: number | null) => void;
    /** Shown while no provider is selected, when an empty selection has a meaning. */
    providerPlaceholder?: string;
    countryCode: ServiceCountryCode | null;
    onCountryChange: (countryCode: ServiceCountryCode) => void;
    countryOptions?: { code: ServiceCountryCode; labelKey: string }[];
    showCountry?: boolean;
    dateRangeControl?: ReactNode;
    countryFilterControl?: ReactNode;
    actionControl?: ReactNode;
}

const BillingScopeToolbar = ({
    view,
    onViewChange,
    providerOptions,
    providersLoading,
    providerId,
    onProviderChange,
    providerPlaceholder,
    countryCode,
    onCountryChange,
    countryOptions = BILLING_COUNTRIES,
    showCountry = true,
    dateRangeControl,
    countryFilterControl,
    actionControl,
}: BillingScopeToolbarProps) => {
    const { translate } = useTranslate();
    const selectedProvider = providerOptions.find((option) => option.id === providerId) ?? null;
    const viewOptions = [
        { id: 'revenue' as const, label: translate('serviceProviderBilling.views.revenue') },
        { id: 'setup' as const, label: translate('serviceProviderBilling.views.setup') },
    ];

    return (
        <Paper
            variant="outlined"
            sx={{ p: 1.5, borderRadius: 3, borderColor: 'divider', bgcolor: 'background.paper' }}
        >
            <Stack
                direction={{ xs: 'column', lg: 'row' }}
                spacing={1.5}
                useFlexGap
                sx={{
                    alignItems: { xs: 'stretch', lg: 'center' },
                    flexWrap: 'wrap',
                }}
            >
                <ViewToggle
                    ariaLabel={translate('serviceProviderBilling.views.aria')}
                    value={view}
                    onChange={onViewChange}
                    options={viewOptions}
                    sx={{ alignSelf: { xs: 'flex-start', lg: 'center' }, flexShrink: 0 }}
                />
                <Autocomplete
                    size="small"
                    options={providerOptions}
                    value={selectedProvider}
                    loading={providersLoading}
                    onChange={(_event, option) => onProviderChange(option?.id ?? null)}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    sx={{ width: { xs: '100%', sm: 300 }, flexShrink: 0 }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={translate('serviceProvider')}
                            placeholder={providerPlaceholder}
                            slotProps={
                                providerPlaceholder
                                    ? {
                                          ...params.slotProps,
                                          inputLabel: {
                                              ...params.slotProps.inputLabel,
                                              shrink: true,
                                          },
                                      }
                                    : params.slotProps
                            }
                        />
                    )}
                />
                {showCountry ? (
                    <ViewToggle
                        ariaLabel={translate('country')}
                        value={countryCode}
                        onChange={onCountryChange}
                        options={countryOptions.map((country) => ({
                            id: country.code,
                            label: translate(country.labelKey),
                        }))}
                        sx={{ alignSelf: { xs: 'flex-start', lg: 'center' }, flexShrink: 0 }}
                    />
                ) : null}
                {dateRangeControl ? (
                    <Box sx={{ alignSelf: { xs: 'stretch', lg: 'center' }, flexShrink: 0 }}>
                        {dateRangeControl}
                    </Box>
                ) : null}
                {countryFilterControl ? (
                    <Box sx={{ alignSelf: { xs: 'flex-start', lg: 'center' }, flexShrink: 0 }}>
                        {countryFilterControl}
                    </Box>
                ) : null}
                {actionControl ? (
                    <Box sx={{ alignSelf: { xs: 'stretch', lg: 'center' }, flexShrink: 0 }}>
                        {actionControl}
                    </Box>
                ) : null}
            </Stack>
        </Paper>
    );
};

export default BillingScopeToolbar;
